/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDERS-SPLIT — Split nach Positionen (S135 P4 Cut 2b)
   =============================================================================
   Flow (Jonas-Entscheid 2026-06-15):
     „Bar/Karte" an einer dine-in-Order → Rückfrage „Rechnung splitten?"
       · Nein → Gesamtrechnung wie heute (orderMarkPaid — UNVERÄNDERT)
       · Ja   → Warenkorb: Positionen je Teilbeleg wählen, je Teil Bar/Karte,
                nacheinander abrechnen („weiter abrechnen?"-Schleife)
     Jeder Teilbeleg: pay_order_split (eigene invoice_number) → signAndRecordReceipt
       (eigene TSE) → Teilbeleg-Bon (dasselbe Bon-Modal, receipt-gebunden via
       Pseudo-Order). Letzter Teil deckt alle Positionen → Order auto-`paid`
       (Reservierungs-Abschluss-Trigger feuert). Rest, den niemand nimmt → Schwund.

   Reuse-first: KEIN dupliziertes receipt.js (Pseudo-Order auf renderOrderReceipt),
   Voll-Zahlpfad (mark_order_paid + signAndRecord) bleibt unangetastet.
============================================================================ */

import { showToast, htmlEscape, fmtEUR } from '../../utils/helpers.js';
import { closeModal, rerenderActiveTab } from '../../utils/dom.js';
import { invalidateItems } from '../../core/order-items-cache.js';
// S200 — Menü-Gruppierung (pure) für gruppiertes Kassieren + Rabatt-Gate.
import { groupByLineUid, hasDiscountableItems } from '../../core/deals.js';
// S248 — derselbe Archiv-Pfad wie Voll-Zahlung und Online-Nachlauf.
import { _ensureBonArchived } from './orders-receipt-actions.js';

/** Pure: sind ALLE offenen Komponenten einer Menü-Gruppe im Split ausgewählt? */
export function isGroupFullySelected(group, selectedSet) {
  const items = (group && group.items) || [];
  return items.length > 0 && items.every(it => selectedSet && selectedSet.has(it.id));
}

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}
function findOrder(id) {
  return (getState().orders || []).find(o => o.id === id) || null;
}
function openItemsOf(items) {
  return (Array.isArray(items) ? items : []).filter(it => !it.receiptId);
}

// — Modul-State (nur während der Split-Warenkorb offen ist) —————————————
let _split = null;   // { orderId, method, selected:Set<itemId>, items:[] }

export const SCHWUND_CATEGORIES = [
  { key: 'reklamation',     label: 'Reklamation',     db: 'customer_complaint' },
  { key: 'fehlzubereitung', label: 'Fehlzubereitung', db: 'preparation_error' },
  { key: 'fehleingabe',     label: 'Fehleingabe',     db: 'wrong_billing' },
  { key: 'verderb',         label: 'Verderb',         db: 'spoilage' },
  { key: 'sonstiges',       label: 'Sonstiges',       db: 'other' }
];

// S143 / Ticket 2 — kontext-Kategorien für den Storno/No-Show-mit-Schwund-Pfad
// (phase_110-CHECK). Werden automatisch passend vorbelegt; eigene Labels für
// „Schwund heute" + Z-Bon. db-Werte exakt wie im DB-CHECK.
export const SCHWUND_CONTEXT_LABELS = {
  storno_after_prep: 'Storno nach Zubereitung',
  no_show:           'No-Show'
};

/**
 * S136 Cut A — Übersetzt eine DB-`reason_category` (order_corrections) in das
 * deutsche Operator-Label. Single-Source für den Schwund-Schreibpfad (oben) und
 * die read-only „Schwund heute"-Liste (orders.js). `out_of_stock` ist im
 * DB-CHECK erlaubt, hat aber keine UI-Kachel → eigenes Label. S143: zusätzlich
 * die beiden Kontext-Kategorien storno_after_prep / no_show.
 */
export function schwundCategoryLabel(dbValue) {
  const hit = SCHWUND_CATEGORIES.find(c => c.db === dbValue);
  if (hit) return hit.label;
  if (SCHWUND_CONTEXT_LABELS[dbValue]) return SCHWUND_CONTEXT_LABELS[dbValue];
  if (dbValue === 'out_of_stock') return 'Nicht vorrätig';
  return 'Schwund';
}

/**
 * S143 / Ticket 2 — automatische Kontext-Kategorie aus der Aktion (pure, testbar).
 * Storno einer zubereiteten Order → „Storno nach Zubereitung", No-Show → „No-Show".
 * @param {'cancelled'|'no_show'} targetStatus
 * @returns {{db:string,label:string}}
 */
export function autoSchwundCategory(targetStatus) {
  const db = targetStatus === 'no_show' ? 'no_show' : 'storno_after_prep';
  return { db, label: SCHWUND_CONTEXT_LABELS[db] };
}

/* --------------------------- Pseudo-Order für den Teilbeleg-Bon ----------- */
/**
 * Baut aus einem Teilbeleg (order_receipts) + seiner Item-Teilmenge eine
 * „Pseudo-Order", die der bestehende Bon-Renderer (renderOrderReceipt /
 * openReceiptActionModal / receipt-pdf) 1:1 verarbeitet — kein receipt.js-
 * Duplikat. occupancy_id/reservation_id bleiben NULL: die Tisch-Freigabe würde
 * im Bon-Modal `o.id` (= receipt.id) als Order-ID nutzen — falsch. Die Freigabe
 * läuft daher separat auf der ELTERN-Order (offerTableReleaseForClosedOrder),
 * sobald die Order vollständig abgerechnet ist. Pure — exportiert für Tests.
 */
export function buildReceiptPseudoOrder(receipt, items, parentOrder) {
  const po = parentOrder || {};
  const snapItems = (Array.isArray(items) ? items : []).map(it => ({
    name:             it.nameSnapshot || it.name_snapshot || it.name || 'Position',
    qty:              Number(it.qty) || 1,
    unit_price_cents: Number(it.unitPriceCents != null ? it.unitPriceCents : it.unit_price_cents) || 0,
    line_total_cents: Number(it.lineTotalCents != null ? it.lineTotalCents : it.line_total_cents) || 0,
    tax_rate:         Number(it.taxRate != null ? it.taxRate : it.tax_rate) || 7,
    note:             it.notes || (it.customizations && it.customizations.note) || null
  }));
  return {
    id:               receipt.id,
    orderNumber:      (po.orderNumber || po.id || '') + ' · Teilbeleg',
    invoiceNumber:    receipt.invoiceNumber,
    totalGrossCents:  Number(receipt.totalGrossCents) || 0,
    tax7Cents:        Number(receipt.tax7AmountCents) || 0,
    tax19Cents:       Number(receipt.tax19AmountCents) || 0,
    subtotalNetCents: Number(receipt.subtotalNetCents) || 0,
    // S179 R3b — Rabatt-Snapshot für die Bon-Rabattzeile (renderOrderReceipt liest discount*).
    discountId:       receipt.discountId || null,
    discountName:     receipt.discountName || null,
    discountPercent:  (receipt.discountPercent != null) ? receipt.discountPercent : null,
    discountCents:    Number(receipt.discountCents) || 0,
    paymentStatus:    'paid',
    paymentMethod:    receipt.paymentMethod,
    // S248 (O2) — `paidAt` ist der Zahlungszeitpunkt, nicht der Anlagezeitpunkt.
    // Bei einem Online-Teilbeleg liegt zwischen beiden die Haltung: `created_at`
    // ist der Moment, in dem der Gast auf „bezahlen" getippt hat, `paid_at` der,
    // in dem das Geld da war. Im Archiv stand deshalb die falsche Uhrzeit.
    paidAt:           receipt.paidAt || receipt.paid_at || receipt.createdAt || new Date().toISOString(),
    customerName:     po.customerName || '',
    customerEmail:    po.customerEmail || '',
    pickupDate:       po.pickupDate || null,
    pickupTime:       po.pickupTime || null,
    occupancyId:      null,   // Freigabe läuft separat auf der Eltern-Order
    reservationId:    null,
    isCorrection:     false,
    tseTransactionId:    receipt.tseTransactionId || null,
    tseSignatureCounter: receipt.tseSignatureCounter || null,
    tseSignatureValue:   receipt.tseSignatureValue || null,
    tseSignedAt:         receipt.tseSignedAt || null,
    tseQrPayload:        receipt.tseQrPayload || null,
    tseMode:             receipt.tseMode || null,
    preorderSnapshot: { version: 2, items: snapItems }
  };
}

/* --------------------------- Einstieg: Bar/Karte ------------------------- */
/**
 * Wird von den „Bar/Karte"-Buttons aufgerufen. dine-in → Split-Rückfrage;
 * sonst (Takeaway/Lieferung) direkt der unveränderte Voll-Zahlpfad.
 */
export async function orderCheckout(orderId, method) {
  const order = findOrder(orderId);
  if (!order) { return window.orderMarkPaid && window.orderMarkPaid(orderId, method); }
  if (order.serviceType !== 'dine-in') {
    // Theke/Takeaway: keine Split-Rückfrage, aber optionaler Rabatt vor der Zahlung.
    return openDiscountThenPay(orderId, method);
  }
  // S246 — „Alles zusammen" läuft über `mark_order_paid`, und das kennt KEINE
  // Belege: es stempelt die ganze Rechnung, egal was daran schon abgerechnet
  // ist. Zwei Zustände dürfen deshalb gar nicht erst zur Auswahl führen —
  // hartes Gate, kein Toast (Memory feedback_status_lifecycle_gates).
  const st = await _receiptState(orderId);
  if (st.holds.length) {
    // Jemand ist gerade bei Stripe: seine Positionen sind reserviert.
    openHoldBlockedModal(orderId, method, st.holds);
    return;
  }
  // S246-HF (P2) — ein Teil ist bereits BEZAHLT (bar gesplittet oder online).
  // Vorher bot die Frage weiter „Alles zusammen (Bar)" an; im Retest hätte das
  // 21,90 € kassiert, obwohl 10,00 € davon längst online bezahlt waren — samt
  // zweitem TSE-Beleg. Es gibt hier nur noch den Rest.
  openSplitDecision(orderId, method, { restOnly: st.paidCount > 0, paid: st });
}

/**
 * Belege einer Order, aufgeteilt in „läuft gerade" und „ist bezahlt".
 *
 * Fehler = leerer Zustand: ein Ausfall des Lookups darf das Kassieren nicht
 * blockieren. Er darf es nur nicht DECKEN — deshalb steht das serverseitige
 * Loch (`mark_order_paid` ignoriert Belege) weiterhin als Ticket.
 */
async function _receiptState(orderId) {
  const empty = { holds: [], paidCount: 0, paidCents: 0 };
  const st = window.App && window.App.storage && window.App.storage.orders;
  if (!st || typeof st.listReceipts !== 'function') return empty;
  try {
    const rows = (await st.listReceipts(orderId)) || [];
    const now = Date.now();
    const holds = rows.filter(r => r.paymentStatus === 'pending'
      && (!r.holdExpiresAt || new Date(r.holdExpiresAt).getTime() > now));
    // Bestandsbelege (vor phase_179) haben kein `paymentStatus` — sie sind
    // bezahlt, sonst gäbe es sie nicht.
    const paid = rows.filter(r => (r.paymentStatus || 'paid') === 'paid');
    return {
      holds,
      paidCount: paid.length,
      paidCents: paid.reduce((s, r) => s + (Number(r.totalGrossCents) || 0), 0)
    };
  } catch (e) {
    console.warn('[orders-split] Belege nicht ladbar:', e && e.message);
    return empty;
  }
}

/* --------------------------- Server-Guard (S247) -------------------------- */
/**
 * Pure — trägt dieser Fehler den Beleg-Guard aus `mark_order_paid` (phase_181)?
 *
 * Der Client prüft vorher selbst (`_receiptState`), aber zwischen Prüfung und
 * Klick liegt eine Sekunde, in der ein Gast bezahlen kann — und ein zweites
 * Gerät mit altem Stand prüft gar nicht. Der Server ist die Wahrheit; hier wird
 * seine Antwort nur wieder in dieselbe Oberfläche übersetzt, damit der Operator
 * keinen rohen Fehlertext liest.
 *
 * @returns {'split'|'hold'|null}
 */
export function classifyPayGuardError(err) {
  const msg = String((err && (err.message || err.hint)) || err || '');
  if (/SPLIT_ALREADY_PAID/.test(msg))    return 'split';
  if (/TABLE_PAY_IN_PROGRESS/.test(msg)) return 'hold';
  return null;
}

/**
 * Den Guard-Fehler in dieselbe Antwort übersetzen, die der Vorab-Check gibt.
 * `true` = übernommen (kein Fehler-Toast mehr), `false` = kein Guard-Fehler.
 */
export async function handlePayGuardError(orderId, method, err) {
  const kind = classifyPayGuardError(err);
  if (!kind) return false;
  const st = await _receiptState(orderId);
  if (kind === 'hold' && st.holds.length) {
    openHoldBlockedModal(orderId, method, st.holds);
  } else {
    // Auch für 'hold' ohne gefundene Haltung: die ist zwischen Fehler und
    // Nachschlagen abgelaufen — dann ist der Rest genau das, was zu kassieren
    // bleibt. Ein leeres „0 Gäste zahlen online" wäre schlicht falsch.
    openSplitDecision(orderId, method, { restOnly: true, paid: st });
  }
  return true;
}

/**
 * Der Tisch zahlt gerade online. Zwei Wege raus: warten (der Normalfall) oder
 * NUR den Rest splitten — die gehaltenen Positionen stehen dort ohnehin nicht
 * zur Auswahl, `pay_order_split` nimmt nur Positionen ohne Beleg.
 */
export function openHoldBlockedModal(orderId, method, holds) {
  const sum = (holds || []).reduce((s, h) => s + (Number(h.totalGrossCents) || 0), 0);
  document.getElementById('modal-content').innerHTML = `
    <div class="modal-header">
      <h2>Tisch zahlt gerade online</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p style="margin:6px 0 16px; color:var(--ink-soft); font-size:.95rem;">
      ${holds.length === 1 ? 'Ein Gast' : holds.length + ' Gäste'} am Tisch
      ${holds.length === 1 ? 'ist' : 'sind'} gerade bei Stripe
      (${htmlEscape(fmtEUR(sum / 100))}). Diese Positionen sind reserviert —
      „Alles zusammen" würde sie ein zweites Mal kassieren.
    </p>
    <div class="btn-row" style="display:flex; gap:10px; flex-wrap:wrap;">
      <button class="btn btn-primary" onclick="closeModal()">Kurz warten</button>
      <button class="btn btn-secondary" onclick="splitDecisionYes('${htmlEscape(orderId)}')">
        🧾 Nur den Rest kassieren
      </button>
    </div>`;
  document.getElementById('modal-overlay').classList.add('active');
}

function openSplitDecision(orderId, method, opts) {
  // S246-HF (P2) — ist an dieser Rechnung schon etwas BEZAHLT, gibt es kein
  // „Alles zusammen" mehr. Der Knopf führte zu `mark_order_paid`, und das
  // stempelt die volle Summe — der bereits bezahlte Teil wäre ein zweites Mal
  // kassiert und ein zweiter TSE-Beleg entstanden.
  const restOnly = !!(opts && opts.restOnly);
  const paid = (opts && opts.paid) || { paidCount: 0, paidCents: 0 };
  const intro = restOnly
    ? `An dieser Rechnung ${paid.paidCount === 1 ? 'ist bereits ein Teil' : 'sind bereits Teile'}
       bezahlt (${htmlEscape(fmtEUR((paid.paidCents || 0) / 100))}).
       Kassiert wird deshalb nur noch <strong>der offene Rest</strong>.`
    : `Möchte der Tisch die Rechnung <strong>splitten</strong> (getrennt zahlen),
       oder alles zusammen?`;
  const html = `
    <div class="modal-header">
      <h2>Kassieren</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p style="margin:6px 0 16px; color:var(--ink-soft); font-size:.95rem;">
      ${intro}
    </p>
    <div class="btn-row" style="display:flex; gap:10px; flex-wrap:wrap;">
      ${restOnly ? '' : `
      <button class="btn btn-primary" onclick="splitDecisionNo('${htmlEscape(orderId)}','${method}')">
        Alles zusammen (${method === 'card' ? 'Karte' : 'Bar'})
      </button>`}
      <button class="btn ${restOnly ? 'btn-primary' : 'btn-secondary'}"
              onclick="splitDecisionYes('${htmlEscape(orderId)}')">
        🧾 ${restOnly ? 'Rest kassieren' : 'Rechnung splitten'}
      </button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>`;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export function splitDecisionNo(orderId, method) {
  // „Alles zusammen" — optionaler Rabatt auf die Gesamtrechnung, dann zahlen.
  openDiscountThenPay(orderId, method);
}

/* ---------------------- Rabatt-Auswahl vor Voll-Zahlung (S179 R3a) -------- */
/**
 * Optionaler Rabatt-Schritt VOR der Voll-Zahlung (Theke/Takeaway + dine-in
 * „alles zusammen"). Lädt aktive Rabatte (discounts_for_pos, kein Rollen-Gate —
 * jeder Operator). Gibt es keine aktiven Rabatte ODER nur Deal-Positionen (Deals
 * sind vom Rabatt ausgeschlossen) → direkt zahlen. Der gewählte Rabatt geht als
 * discountId an orderMarkPaid → mark_order_paid mindert die Totals GoBD-korrekt
 * server-seitig VOR der TSE-Signatur.
 */
export async function openDiscountThenPay(orderId, method) {
  const supa = window.App && window.App.supabase;
  let discounts = [];
  try {
    if (supa) { const { data, error } = await supa.rpc('discounts_for_pos'); if (!error) discounts = Array.isArray(data) ? data : []; }
  } catch (_) { discounts = []; }

  // Gibt es rabattierbare (Nicht-Deal-)Positionen? Deals sind ausgeschlossen (S179 R3).
  let hasDiscountable = true;
  try {
    const items = await window.App.storage.orders.listItems(orderId);
    hasDiscountable = hasDiscountableItems(items);
  } catch (_) { hasDiscountable = true; }

  if (!discounts.length || !hasDiscountable) {
    closeModal();
    if (typeof window.orderMarkPaid === 'function') window.orderMarkPaid(orderId, method);
    return;
  }

  const methodLabel = method === 'card' ? 'Karte' : 'Bar';
  const rows = discounts.map(d => `
    <button class="btn btn-secondary" style="justify-content:space-between; display:flex; width:100%;"
            onclick="discountPayWith('${htmlEscape(orderId)}','${method}','${htmlEscape(d.id)}')">
      <span>${htmlEscape(d.name)}</span><strong>−${htmlEscape(String(d.percent).replace('.', ','))} %</strong>
    </button>`).join('');

  const html = `
    <div class="modal-header">
      <h2>Rabatt anwenden?</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p style="margin:6px 0 14px; color:var(--ink-soft); font-size:.95rem;">
      Optional einen Rabatt auf diese Rechnung anwenden (Deals sind ausgenommen),
      dann mit <strong>${methodLabel}</strong> kassieren.
    </p>
    <div class="btn-row" style="display:flex; flex-direction:column; gap:8px;">
      ${rows}
      <button class="btn btn-primary" onclick="discountPayNone('${htmlEscape(orderId)}','${method}')">
        Ohne Rabatt · ${methodLabel} kassieren
      </button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>`;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export function discountPayNone(orderId, method) {
  closeModal();
  if (typeof window.orderMarkPaid === 'function') window.orderMarkPaid(orderId, method);
}
export function discountPayWith(orderId, method, discountId) {
  closeModal();
  if (typeof window.orderMarkPaid === 'function') window.orderMarkPaid(orderId, method, discountId);
}
export function splitDecisionYes(orderId) {
  openSplitCart(orderId);
}

/* --------------------------- Warenkorb ----------------------------------- */
export async function openSplitCart(orderId) {
  const order = findOrder(orderId);
  if (!order) return;
  let items = [];
  try {
    items = await window.App.storage.orders.listItems(orderId);
  } catch (e) {
    console.error('[orders-split] listItems', e);
    return showToast('Positionen konnten nicht geladen werden.', 'error');
  }
  const open = openItemsOf(items);
  if (!open.length) {
    closeModal();
    return showToast('Alle Positionen sind bereits abgerechnet.', 'success');
  }
  // S179 R3b — aktive Rabatte einmal laden (für den optionalen Teilbeleg-Rabatt).
  let discounts = (_split && _split.orderId === orderId && Array.isArray(_split.discounts)) ? _split.discounts : null;
  if (!discounts) {
    discounts = [];
    try {
      const supa = window.App && window.App.supabase;
      if (supa) { const { data, error } = await supa.rpc('discounts_for_pos'); if (!error) discounts = Array.isArray(data) ? data : []; }
    } catch (_) { discounts = []; }
  }
  _split = {
    orderId,
    method: (_split && _split.orderId === orderId) ? _split.method : 'cash',
    discountId: (_split && _split.orderId === orderId) ? (_split.discountId || null) : null,
    discounts,
    selected: new Set(),
    // S200 — line_uids, die für DIESEN Split-Vorgang „aufgetrennt" sind (Session-
    // State, KEIN DB-Write, _dealMeta bleibt unangetastet). Über openSplitCart-Runden
    // erhalten (weiter abrechnen), aber pro Order frisch.
    splitMenus: (_split && _split.orderId === orderId && _split.splitMenus) ? _split.splitMenus : new Set(),
    items
  };
  renderSplitCart();
}

function renderSplitCart() {
  if (!_split) return;
  const order = findOrder(_split.orderId);
  const open = openItemsOf(_split.items);
  const paidCount = (_split.items.length - open.length);
  const selectedTotal = open
    .filter(it => _split.selected.has(it.id))
    .reduce((s, it) => s + (Number(it.lineTotalCents) || 0), 0);

  // S200 — Positionen nach Menü-line_uid gruppieren: ganzes Menü wandert als EINE
  // Geste (Gruppen-Checkbox), es sei denn, es ist für diesen Vorgang „aufgetrennt".
  const itemRow = (it) => {
    const checked = _split.selected.has(it.id);
    const name = htmlEscape(it.nameSnapshot || it.name || 'Position');
    return `
      <label class="split-item ${checked ? 'is-sel' : ''}">
        <input type="checkbox" ${checked ? 'checked' : ''} onclick="splitToggleItem('${htmlEscape(it.id)}')">
        <span class="split-item-qty">${Number(it.qty) || 1}×</span>
        <span class="split-item-name">${name}</span>
        <span class="split-item-price">${fmtEUR((Number(it.lineTotalCents) || 0) / 100)}</span>
      </label>`;
  };
  const compLine = (it) => `
      <div class="split-comp">
        <span class="split-item-qty">${Number(it.qty) || 1}×</span>
        <span class="split-item-name">${htmlEscape(it.nameSnapshot || it.name || 'Position')}</span>
        <span class="split-item-price">${fmtEUR((Number(it.lineTotalCents) || 0) / 100)}</span>
      </div>`;

  const rows = groupByLineUid(open).map(entry => {
    if (entry.type === 'single') return itemRow(entry.item);
    const g = entry;
    const gid = htmlEscape(g.lineUid);
    const gTotal = g.items.reduce((s, it) => s + (Number(it.lineTotalCents) || 0), 0);
    const splittable = g.items.length > 1;
    if (_split.splitMenus.has(g.lineUid)) {
      // Aufgetrennt: Komponenten einzeln wählbar wie heute + Wieder-Zusammenfassen.
      return `
        <div class="split-menu is-broken">
          <div class="split-menu-head">
            <span>🎁 ${htmlEscape(g.dealName)} <small>· aufgetrennt</small></span>
            <button type="button" class="btn btn-ghost btn-xs" onclick="splitMergeMenu('${gid}')">🔗 wieder zusammenfassen</button>
          </div>
          ${g.items.map(itemRow).join('')}
        </div>`;
    }
    // Als Menü: Gruppen-Checkbox (ganze Geste) + eingerückte Komponenten (read-only).
    const allSel = isGroupFullySelected(g, _split.selected);
    return `
      <div class="split-menu ${allSel ? 'is-sel' : ''}">
        <label class="split-item split-menu-head">
          <input type="checkbox" ${allSel ? 'checked' : ''} onclick="splitToggleGroup('${gid}')">
          <span class="split-item-name">🎁 ${htmlEscape(g.dealName)}</span>
          <span class="split-item-price">${fmtEUR(gTotal / 100)}</span>
        </label>
        <div class="split-menu-comps">${g.items.map(compLine).join('')}</div>
        ${splittable ? `<button type="button" class="btn btn-ghost btn-xs split-menu-break" onclick="splitBreakMenu('${gid}')">✂️ Menü auftrennen</button>` : ''}
      </div>`;
  }).join('');

  const html = `
    <div class="modal-header">
      <h2>🧾 Rechnung splitten — Tisch ${htmlEscape(String(order && (order.orderNumber || order.id) || ''))}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p style="margin:4px 0 10px; color:var(--ink-soft); font-size:.9rem;">
      Positionen für diesen Teilbeleg wählen, Zahlart setzen, „Teil kassieren".
      ${paidCount > 0 ? `<strong>${paidCount}</strong> bereits abgerechnet · ` : ''}<strong>${open.length}</strong> offen.
    </p>
    <div class="split-items">${rows}</div>
    <div class="split-method" style="display:flex; gap:8px; align-items:center; margin:12px 0;">
      <span style="font-size:.9rem;">Zahlart Teilbeleg:</span>
      <button class="btn btn-sm ${_split.method === 'cash' ? 'btn-primary' : 'btn-ghost'}" onclick="splitSetMethod('cash')">💶 Bar</button>
      <button class="btn btn-sm ${_split.method === 'card' ? 'btn-primary' : 'btn-ghost'}" onclick="splitSetMethod('card')">💳 Karte</button>
    </div>
    ${(Array.isArray(_split.discounts) && _split.discounts.length) ? `
    <div class="split-discount" style="display:flex; gap:8px; align-items:center; margin:0 0 12px;">
      <span style="font-size:.9rem;">Rabatt (optional, Deals ausgenommen):</span>
      <select onchange="splitSetDiscount(this.value)">
        <option value="" ${!_split.discountId ? 'selected' : ''}>Kein Rabatt</option>
        ${_split.discounts.map(d => `<option value="${htmlEscape(d.id)}" ${_split.discountId === d.id ? 'selected' : ''}>${htmlEscape(d.name)} (−${htmlEscape(String(d.percent).replace('.', ','))} %)</option>`).join('')}
      </select>
    </div>` : ''}
    <div class="btn-row" style="display:flex; gap:8px; flex-wrap:wrap; margin-top:8px;">
      <button class="btn btn-primary" ${selectedTotal > 0 ? '' : 'disabled style="opacity:.5;cursor:not-allowed;"'}
              onclick="splitPayPart()">
        Teil kassieren (${fmtEUR(selectedTotal / 100)})
      </button>
      <button class="btn btn-ghost" style="color:var(--danger);" onclick="splitOpenSchwund()">
        Rest als Schwund
      </button>
      <button class="btn btn-ghost" onclick="closeModal()">Später</button>
    </div>`;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export function splitToggleItem(itemId) {
  if (!_split) return;
  if (_split.selected.has(itemId)) _split.selected.delete(itemId);
  else _split.selected.add(itemId);
  renderSplitCart();
}

/** S200 — ganze Menü-Gruppe auswählen/abwählen (eine Geste). Alle offenen
 *  Komponenten des line_uid gemeinsam. */
export function splitToggleGroup(lineUid) {
  if (!_split) return;
  const open = openItemsOf(_split.items);
  const groupItems = open.filter(it => it.customizations && it.customizations._dealMeta && it.customizations._dealMeta.line_uid === lineUid);
  if (!groupItems.length) return;
  const allSel = groupItems.every(it => _split.selected.has(it.id));
  groupItems.forEach(it => { if (allSel) _split.selected.delete(it.id); else _split.selected.add(it.id); });
  renderSplitCart();
}

/** S200 — Menü NUR für diesen Split-Vorgang auftrennen (Session-State, kein DB-Write,
 *  `_dealMeta` bleibt) → Komponenten einzeln wählbar. */
export function splitBreakMenu(lineUid) {
  if (!_split) return;
  _split.splitMenus.add(lineUid);
  renderSplitCart();
}

/** S200 — Auftrennung rückgängig (wieder als Menü). Saubere Startlage: Komponenten
 *  des Menüs abwählen, damit kein verstecktes Teil-Selektions-Chaos entsteht. */
export function splitMergeMenu(lineUid) {
  if (!_split) return;
  _split.splitMenus.delete(lineUid);
  openItemsOf(_split.items)
    .filter(it => it.customizations && it.customizations._dealMeta && it.customizations._dealMeta.line_uid === lineUid)
    .forEach(it => _split.selected.delete(it.id));
  renderSplitCart();
}
export function splitSetMethod(method) {
  if (!_split) return;
  _split.method = (method === 'card') ? 'card' : 'cash';
  renderSplitCart();
}
export function splitSetDiscount(discountId) {
  if (!_split) return;
  _split.discountId = discountId || null;
  renderSplitCart();
}

export async function splitPayPart() {
  if (!_split) return;
  const order = findOrder(_split.orderId);
  const open = openItemsOf(_split.items);
  const partItems = open.filter(it => _split.selected.has(it.id));
  if (!partItems.length) return;
  const itemIds = partItems.map(it => it.id);
  const method = _split.method;
  const orderId = _split.orderId;

  const discountId = _split.discountId || null;
  let receipt;
  try {
    receipt = await window.App.storage.orders.payOrderSplit(orderId, itemIds, method, discountId);
  } catch (err) {
    console.error('[orders-split] payOrderSplit', err);
    if (!(err && err.operatorRequired)) showToast('Teil-Kassieren fehlgeschlagen: ' + (err.message || err), 'error');
    return;
  }
  // TSE je Teilbeleg (Cut 2a). disabled → null → ohne Signatur weiter.
  try {
    if (typeof window.tseSignAndRecordReceipt === 'function') {
      // S241 / HF-10 — die Zeilen DIESES Teilbelegs mitgeben: sie bestimmen das
      // Brutto je Steuersatz in den signierten Prozessdaten. Ohne sie fiele die
      // Signatur auf die ungenaue Rückrechnung aus der gerundeten Steuer zurück.
      const res = await window.tseSignAndRecordReceipt(receipt, { items: partItems });
      if (res && res.receiptRow) receipt = { ...receipt, ...mapSignedReceipt(res.receiptRow) };
      else if (res && res.sig) receipt = { ...receipt, ...sigToReceiptFields(res.sig) };
    }
  } catch (tseErr) {
    console.warn('[orders-split] TSE-Signatur Teilbeleg fehlgeschlagen (nicht-fatal):', tseErr);
    showToast('TSE-Signatur fehlgeschlagen: ' + (tseErr.message || tseErr), 'error');
  }

  invalidateItems(orderId);
  // Frische Items → bleibt etwas offen?
  try {
    const fresh = await window.App.storage.orders.listItems(orderId);
    _split.items = fresh;
  } catch (_) { /* nicht-fatal */ }

  const pseudo = buildReceiptPseudoOrder(receipt, partItems, order);

  // S248 (O1) — GoBD-Archiv für den Teilbeleg. Das Belegausgabe-Modal sagte
  // „Bereits automatisch archiviert", aber im Split-Pfad archivierte NIEMAND:
  // live lagen 0 von 17 Bar/Karte-Teilbelegen im Archiv. Derselbe Aufruf wie im
  // Voll-Zahlpfad und im Online-Nachlauf — `insertOnly`, damit ein zweites
  // Gerät oder ein späterer Nachlauf keine zweite Zeile schreibt.
  try {
    await _ensureBonArchived(pseudo, { source: 'split', insertOnly: true });
  } catch (arcErr) {
    // Nicht-fatal: das Geld ist kassiert, der Beleg existiert. Der Nachlauf im
    // Archiv holt ihn nach — blockieren würde hier nur den Bezahl-Flow.
    console.warn('[orders-split] Bon-Archivierung fehlgeschlagen (Nachlauf greift):', arcErr);
  }

  closeModal();
  const _rDisc = Number(receipt.discountCents) || 0;
  const _rDiscNote = _rDisc > 0 ? ` · Rabatt ${receipt.discountName ? receipt.discountName + ' ' : ''}−${fmtEUR(_rDisc / 100)}` : '';
  showToast(`Teilbeleg ${receipt.invoiceNumber || ''} abgerechnet (${fmtEUR((Number(receipt.totalGrossCents) || 0) / 100)})${_rDiscNote}.`, 'success');
  if (typeof window.openReceiptActionModal === 'function') {
    window.openReceiptActionModal(pseudo, { kind: 'paid', onClose: () => afterSplitPart(orderId) });
  } else {
    afterSplitPart(orderId);
  }
}

function afterSplitPart(orderId) {
  const fresh = (_split && _split.orderId === orderId) ? _split.items : null;
  const remaining = openItemsOf(fresh || []);
  if (remaining.length > 0) {
    openSplitCart(orderId);              // weiter abrechnen
  } else {
    const order = findOrder(orderId);
    _split = null;
    if (typeof window.refreshOrdersTabCounter === 'function') window.refreshOrdersTabCounter();
    showToast('Tisch vollständig abgerechnet.', 'success');
    offerTableReleaseForClosedOrder(order);
  }
}

/* --------------------------- Tisch-Freigabe nach Abschluss --------------- */
/**
 * Split-/Schwund-Abschluss markiert die Order via RPC als `paid` — der
 * Voll-Zahl-Hook (orderMarkPaid → auto_end_occupancy_for_order) läuft NICHT.
 * Daher hier die S134c-Freigabe direkt auf der Eltern-Order anbieten.
 */
function offerTableReleaseForClosedOrder(order) {
  if (!order || !(order.occupancyId || order.reservationId)) return;
  const html = `
    <div class="modal-header">
      <h2>🍽️ Tisch freigeben?</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p style="margin:6px 0 16px; color:var(--ink-soft); font-size:.95rem;">
      Der Tisch ist vollständig abgerechnet. Soll er jetzt wieder buchbar sein?
    </p>
    <div class="btn-row" style="display:flex; gap:10px; flex-wrap:wrap;">
      <button class="btn btn-primary" onclick="splitReleaseTable('${htmlEscape(order.id)}')">Ja, Tisch freigeben</button>
      <button class="btn btn-ghost" onclick="closeModal()">Nein, später</button>
    </div>`;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export async function splitReleaseTable(orderId) {
  const order = findOrder(orderId);
  if (!order) { return closeModal(); }
  try {
    if (order.occupancyId && window.App.supabase) {
      const { error } = await window.App.supabase.rpc('auto_end_occupancy_for_order', { p_order_id: order.id });
      if (error) throw new Error(error.message || 'auto_end_occupancy_for_order');
    } else if (order.reservationId) {
      await window.App.storage.reservations.update(order.reservationId, { tableReleasedAt: new Date().toISOString() });
    }
    showToast('Tisch freigegeben.', 'success');
  } catch (err) {
    console.error('[orders-split] releaseTable', err);
    showToast('Konnte Tisch nicht freigeben: ' + (err.message || err), 'error');
  }
  closeModal();
}

/* --------------------------- Schwund (Rest) ------------------------------ */
export function splitOpenSchwund() {
  if (!_split) return;
  const open = openItemsOf(_split.items);
  if (!open.length) return;
  const total = open.reduce((s, it) => s + (Number(it.lineTotalCents) || 0), 0);
  const list = open.map(it => `${Number(it.qty) || 1}× ${htmlEscape(it.nameSnapshot || it.name || 'Position')}`).join(' · ');
  const cats = SCHWUND_CATEGORIES.map(c =>
    `<button class="btn btn-secondary btn-sm" onclick="splitWriteOff('${c.key}')">${htmlEscape(c.label)}</button>`
  ).join(' ');
  const html = `
    <div class="modal-header">
      <h2>Rest als Schwund erfassen</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div style="background:var(--danger-soft);border-left:3px solid var(--danger);padding:10px 12px;margin-bottom:12px;font-size:.9rem;">
      ⚠️ <strong>Interner Schwund-Beleg, KEIN Kassenbeleg / keine TSE.</strong>
      ${open.length} offene Position${open.length > 1 ? 'en' : ''} (${fmtEUR(total / 100)}) werden als unbezahlt
      abgeschrieben und der Tisch abgeschlossen. Bezahlte Ware zurücknehmen läuft NICHT hier, sondern über den
      Stornobeleg.
    </div>
    <div class="notes" style="margin-bottom:12px;">${list}</div>
    <p style="font-size:.9rem; margin-bottom:8px;"><strong>Grund wählen:</strong></p>
    <div class="btn-row" style="display:flex; gap:8px; flex-wrap:wrap;">${cats}</div>
    <div class="btn-row" style="margin-top:14px;">
      <button class="btn btn-ghost" onclick="splitBackToCart()">← Zurück</button>
    </div>`;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export function splitBackToCart() {
  if (_split) renderSplitCart();
  else closeModal();
}

export async function splitWriteOff(categoryKey) {
  if (!_split) return;
  const cat = SCHWUND_CATEGORIES.find(c => c.key === categoryKey) || SCHWUND_CATEGORIES[SCHWUND_CATEGORIES.length - 1];
  const orderId = _split.orderId;
  const open = openItemsOf(_split.items);
  if (!open.length) return;
  const itemIds = open.map(it => it.id);
  const order = findOrder(orderId);
  try {
    // S137b — KEIN synthetischer Reason mehr: die Kategorie wird im Z-Bon ohnehin
    // als Label gerendert; `reason` ist für optionalen Operator-Freitext reserviert
    // (aktuell keiner) → sonst doppelt „Verderb · 1× — Schwund: Verderb".
    await window.App.storage.orders.writeOffSchwund(orderId, itemIds, cat.db, null);
  } catch (err) {
    console.error('[orders-split] writeOffSchwund', err);
    if (!(err && err.operatorRequired)) showToast('Schwund-Erfassung fehlgeschlagen: ' + (err.message || err), 'error');
    return;
  }
  invalidateItems(orderId);
  // S136 Cut A — „Schwund heute"-Block same-day neu laden (window-Bridge, kein Import-Zyklus).
  if (typeof window.invalidateSchwundToday === 'function') window.invalidateSchwundToday();
  _split = null;
  closeModal();
  showToast(`Rest als Schwund (${cat.label}) erfasst — Tisch abgeschlossen.`, 'success');
  if (typeof window.refreshOrdersTabCounter === 'function') window.refreshOrdersTabCounter();
  // Tisch-Freigabe (Order ist jetzt geschlossen): S134c-Freigabe auf der Order.
  offerTableReleaseForClosedOrder(order);
}

/* ----------------- S143 / Ticket 2 — Storno/No-Show mit Schwund ----------- */
// Eigener Mini-State (getrennt vom Kassier-`_split`): nur offen, solange das
// Schwund-Grund-Modal für eine zubereitete Order angezeigt wird.
let _schwundCancel = null; // { orderId, targetStatus, items, order }

/**
 * Öffnet das Grund-Kategorie-Modal für den Storno/No-Show einer **zubereiteten**
 * Order. Nach Grund-Wahl bucht `cancel_order_with_schwund` (phase_110) die offenen
 * Positionen als Warenverlust (order_corrections, kein TSE) UND setzt den Status
 * (cancelled/no_show) — kein paid. Reuse des verifizierten SCHWUND_CATEGORIES-Sets
 * und derselben GoBD-Optik wie der dedizierte „Rest als Schwund"-Pfad.
 *
 * @param {string} orderId
 * @param {'cancelled'|'no_show'} targetStatus
 * @param {object} [preOrder] vom Aufrufer bereits aufgelöste Order (orders.js
 *   hat sie schon) — vermeidet einen zweiten, evtl. stale Cache-Lookup zwischen
 *   Aktion und Modal (S143c FIX 3, GoBD-kritischer stiller No-Op).
 */
export async function openSchwundCancelModal(orderId, targetStatus, preOrder) {
  const order = preOrder || findOrder(orderId);
  // S143c FIX 3 — kein stiller Return mehr: ist die Order wirklich nicht
  // auffindbar (stale/leerer Cockpit-Cache), klar melden statt das Modal
  // kommentarlos nicht zu öffnen (Schwund-Buchung ist GoBD-kritisch).
  if (!order) {
    return showToast('Bestellung nicht gefunden — bitte die Ansicht neu laden und erneut versuchen.', 'error');
  }
  let items = [];
  try {
    items = await window.App.storage.orders.listItems(orderId);
  } catch (e) {
    console.error('[orders-split] openSchwundCancelModal listItems', e);
    return showToast('Positionen konnten nicht geladen werden.', 'error');
  }
  _schwundCancel = { orderId, targetStatus, items, order };
  const open = openItemsOf(items);
  const total = open.reduce((s, it) => s + (Number(it.lineTotalCents) || 0), 0);
  const list = open.map(it => `${Number(it.qty) || 1}× ${htmlEscape(it.nameSnapshot || it.name || 'Position')}`).join(' · ')
    || '<em>keine offenen Positionen</em>';
  const actionLabel = targetStatus === 'no_show' ? 'No-Show' : 'Storno';
  const num = htmlEscape(String(order.orderNumber || order.order_number || order.id || ''));
  // Auto-Kontext-Grund (Ein-Klick, passend zur Aktion) — prominent vorbelegt.
  const auto = autoSchwundCategory(targetStatus);
  // Granulare Override-Gründe für den Spezialfall (z.B. Fehlzubereitung).
  const cats = SCHWUND_CATEGORIES.map(c =>
    `<button class="btn btn-secondary btn-sm" onclick="schwundCancelWriteOff('${c.key}')">${htmlEscape(c.label)}</button>`
  ).join(' ');
  const html = `
    <div class="modal-header">
      <h2>${actionLabel} — zubereitete Ware als Schwund</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div style="background:var(--danger-soft);border-left:3px solid var(--danger);padding:10px 12px;margin-bottom:12px;font-size:.9rem;">
      ⚠️ <strong>Bestellung ${num} ist bereits zubereitet.</strong> Die Ware wird als
      <strong>Warenverlust (Schwund)</strong> verbucht — <strong>KEIN Kassenbeleg / keine TSE, kein Umsatz</strong>.
      Die Bestellung wird auf <strong>${actionLabel === 'No-Show' ? 'No-Show' : 'storniert'}</strong> gesetzt.
      ${open.length} Position${open.length > 1 ? 'en' : ''} (${fmtEUR(total / 100)}).
    </div>
    <div class="notes" style="margin-bottom:12px;">${list}</div>
    <button class="btn btn-primary" style="width:100%; margin-bottom:12px;" onclick="schwundCancelWriteOff('__auto__')">
      ✓ Als Schwund buchen — Grund „${htmlEscape(auto.label)}"
    </button>
    <details style="margin-bottom:8px;">
      <summary style="cursor:pointer; font-size:.88rem; color:var(--ink-soft);">Anderen Grund wählen</summary>
      <div class="btn-row" style="display:flex; gap:8px; flex-wrap:wrap; margin-top:8px;">${cats}</div>
    </details>
    <div class="btn-row" style="margin-top:6px;">
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>`;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

/** Grund gewählt → cancel_order_with_schwund + UI-Refresh. Storno schickt die
 *  Storno-Mail (Parität zu orderCancel), No-Show nicht. */
export async function schwundCancelWriteOff(categoryKey) {
  if (!_schwundCancel) return;
  const { orderId, targetStatus } = _schwundCancel;
  // '__auto__' = Kontext-Grund aus der Aktion (Storno nach Zubereitung / No-Show),
  // sonst granularer Override aus SCHWUND_CATEGORIES.
  const cat = categoryKey === '__auto__'
    ? autoSchwundCategory(targetStatus)
    : (SCHWUND_CATEGORIES.find(c => c.key === categoryKey) || autoSchwundCategory(targetStatus));
  let updated;
  try {
    updated = await window.App.storage.orders.cancelOrderAsSchwund(orderId, targetStatus, cat.db, null);
  } catch (err) {
    console.error('[orders-split] cancelOrderAsSchwund', err);
    if (!(err && err.operatorRequired)) showToast('Schwund-Storno fehlgeschlagen: ' + (err.message || err), 'error');
    return;
  }
  invalidateItems(orderId);
  // „Schwund heute"-Block same-day neu laden (window-Bridge, kein Import-Zyklus).
  if (typeof window.invalidateSchwundToday === 'function') window.invalidateSchwundToday();
  _schwundCancel = null;
  closeModal();
  // Mail-Parität: Storno-Mail nur bei cancelled (No-Show schickt keine Mail).
  if (targetStatus === 'cancelled' && updated && window.App && window.App.events) {
    try { await window.App.events.emit('order:cancelled', { order: updated, by: 'restaurant' }); } catch (_) { /* nicht-fatal */ }
  }
  const label = targetStatus === 'no_show' ? 'No-Show' : 'Storno';
  showToast(`${label} erfasst · Ware als Schwund (${cat.label}) gebucht`, 'success');
  if (typeof window.refreshOrdersTabCounter === 'function') window.refreshOrdersTabCounter();
  rerenderActiveTab();
}

/* --------------------------- kleine Helfer ------------------------------- */
function sigToReceiptFields(sig) {
  return {
    tseTransactionId:    sig.txNumber,
    tseSignatureCounter: sig.signatureCounter,
    tseSignatureValue:   sig.signatureValue,
    tseSignedAt:         sig.timeEnd,
    tseQrPayload:        sig.qrPayload,
    tseMode:             sig.mode
  };
}
function mapSignedReceipt(row) {
  // record_receipt_tse_signature gibt die Roh-Row zurück (snake_case).
  return {
    tseTransactionId:    row.tse_transaction_id,
    tseSignatureCounter: row.tse_signature_counter,
    tseSignatureValue:   row.tse_signature_value,
    tseSignedAt:         row.tse_signed_at,
    tseQrPayload:        row.tse_qr_payload,
    tseMode:             row.tse_mode
  };
}

/* --------------------------- window-Bindings ----------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, {
    orderCheckout,
    handlePayGuardError,
    openDiscountThenPay,
    discountPayNone,
    discountPayWith,
    splitDecisionNo,
    splitDecisionYes,
    openSplitCart,
    splitToggleItem,
    splitToggleGroup,   // S200
    splitBreakMenu,     // S200
    splitMergeMenu,     // S200
    splitSetMethod,
    splitSetDiscount,
    splitPayPart,
    splitOpenSchwund,
    splitBackToCart,
    splitWriteOff,
    splitReleaseTable,
    openSchwundCancelModal,
    schwundCancelWriteOff
  });
}
